// Sorts server-backed lists immediately, or dispatches changes for a deferred parent form.
import Sortable from '@stimulus-components/sortable';

export default class extends Sortable {
  static targets = ['item', 'status'];

  static values = {
    forceFallback: { type: Boolean, default: false },
    deferred: { type: Boolean, default: false },
    updatedAt: String
  };

  connect() {
    super.connect();
    this.connected = true;
    this.#markReady();
  }

  disconnect() {
    this.connected = false;
    cancelAnimationFrame(this.readyFrame);
    delete this.element.dataset.sortableReady;
    cancelAnimationFrame(this.sortByPositionsFrame);
    this.sortable?.destroy();
    this.sortable = undefined;
  }

  updatedAtValueChanged() {
    if (!this.connected) return;
    this.#markReady();
    this.sortByPositions();
  }

  onUpdate({ item }) {
    this.#syncPositions();
    this.#announcePosition(item);
    this.#submitPosition(item);
  }

  moveItem(event) {
    const offset = event.params?.offset ?? {
      ArrowDown: 1,
      ArrowLeft: -1,
      ArrowRight: 1,
      ArrowUp: -1
    }[event.key];
    if (!offset) return;

    const handle = event.currentTarget;
    const item = handle.closest('[data-sortable-target~="item"]');
    const items = this.itemTargets.filter((candidate) => candidate.dataset.sortablePosition);
    const currentIndex = items.indexOf(item);
    const nextIndex = Math.max(0, Math.min(items.length - 1, currentIndex + offset));
    if (currentIndex < 0 || nextIndex === currentIndex) return;

    event.preventDefault();
    const sibling = items[nextIndex];
    if (offset < 0) {
      this.element.insertBefore(item, sibling);
    } else {
      this.element.insertBefore(item, sibling.nextSibling);
    }

    this.#syncPositions();
    this.#announcePosition(item);
    this.#submitPosition(item);
    const focusTarget = handle.closest('.dropdown')?.querySelector('[data-bs-toggle="dropdown"]') || handle;
    requestAnimationFrame(() => focusTarget.focus());
  }

  sortByPositions() {
    cancelAnimationFrame(this.sortByPositionsFrame);
    this.sortByPositionsFrame = requestAnimationFrame(() => this.reorderByPositions());
  }

  reorderByPositions() {
    const items = this.itemTargets.filter((item) => item.dataset.sortablePosition);
    const sortedItems = [...items].sort((a, b) => {
      return Number(a.dataset.sortablePosition) - Number(b.dataset.sortablePosition);
    });

    if (items.every((item, index) => item === sortedItems[index])) return;

    const startingRects = new Map(items.map((item) => [item, item.getBoundingClientRect()]));

    // Keep fixed content before and after the sortable items in place.
    const afterItems = items.at(-1)?.nextSibling;
    sortedItems.forEach((item) => this.element.insertBefore(item, afterItems || null));

    sortedItems.forEach((item) => {
      const startingRect = startingRects.get(item);
      const endingRect = item.getBoundingClientRect();
      const x = startingRect.left - endingRect.left;
      const y = startingRect.top - endingRect.top;

      if (x === 0 && y === 0) return;

      item.animate([{ transform: `translate(${x}px, ${y}px)` }, { transform: 'translate(0, 0)' }], {
        duration: this.animationValue || 150,
        easing: 'ease-out'
      });
    });
  }

  get options() {
    return {
      ...super.options,
      draggable: '[data-sortable-target~="item"]',
      ghostClass: 'sortable-ghost',
      forceFallback: this.forceFallbackValue,
      fallbackTolerance: 3,
      revertOnSpill: true
    };
  }

  #syncPositions() {
    this.itemTargets.forEach((item, index) => {
      const position = index + 1;
      item.dataset.sortablePosition = position;
      const input = this.#positionInput(item);
      if (input) input.value = position;
    });
  }

  #announcePosition(item) {
    if (!this.hasStatusTarget) return;

    const position = this.itemTargets.indexOf(item) + 1;
    const label = item.dataset.sortableLabel || 'Item';
    this.statusTarget.textContent = `Moved ${label} to position ${position} of ${this.itemTargets.length}.`;
  }

  #submitPosition(item) {
    if (this.deferredValue) {
      this.dispatch('changed');
    } else {
      const input = this.#positionInput(item);
      const link = item.querySelector('[data-sortable-position-link]');
      if (input && link) {
        const url = new URL(link.href);
        url.searchParams.set(link.dataset.sortablePositionParam, input.value);
        link.href = url.toString();
        link.click();
      } else {
        input?.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  }

  #positionInput(item) {
    return item.querySelector('[data-sortable-position-input]');
  }

  #markReady() {
    cancelAnimationFrame(this.readyFrame);
    delete this.element.dataset.sortableReady;
    this.readyFrame = requestAnimationFrame(() => {
      if (this.connected) this.element.dataset.sortableReady = 'true';
    });
  }
}
